import { Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { ArrowLeft } from "lucide-react";

// v77.67 — Privacy Policy rewritten for Ireland / EU GDPR.
// Tone: clear, professional, plain English. Mobile-first readability with
// short paragraphs and bullet points.

const Section = ({ title, id, children }) => (
  <section className="mt-10" id={id}>
    <h2 className="font-heading text-xl font-medium text-[#2E3A3F]">{title}</h2>
    <div className="mt-3 space-y-3 text-[#2E3A3F] leading-relaxed">{children}</div>
  </section>
);

const Sub = ({ title, children }) => (
  <div className="mt-4">
    <h3 className="font-heading text-base font-medium text-[#2E3A3F]">{title}</h3>
    <div className="mt-2 space-y-2">{children}</div>
  </div>
);

export default function Privacy() {
  return (
    <div className="min-h-screen bg-[#F5F7F8] text-[#2E3A3F]">
      <header className="border-b border-[#D9E1E3] bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="privacy-brand">
            <Logo size={32} />
            <span className="font-heading font-semibold text-lg">CoParent Console</span>
          </Link>
          <Link to="/" className="text-sm text-[#6F828A] hover:text-[#1F9FA4] inline-flex items-center gap-1" data-testid="privacy-back-home"><ArrowLeft className="w-4 h-4" /> Home</Link>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="privacy-page">
        <div>
          <div className="text-xs uppercase tracking-[0.12em] text-[#6F828A]">Legal</div>
          <h1 className="font-heading text-4xl font-semibold mt-2">Privacy Policy</h1>
          <p className="text-sm text-[#6F828A] mt-2" data-testid="privacy-last-updated">Last updated: 29 April 2026</p>
        </div>

        <Section title="1. Who we are" id="who-we-are">
          <p>
            CoParent Console (the &quot;Service&quot;, &quot;we&quot;, &quot;us&quot;,
            &quot;our&quot;) is operated from Ireland and is built for separated and
            divorced parents who need a calm, structured way to co-parent.
          </p>
          <p>
            CoParent Console is the data controller for the personal data we
            process about you. For any privacy or data-protection questions,
            contact us at{" "}
            <a href="mailto:support@coparentconsole.com" className="text-[#1F9FA4] hover:text-[#178085]" data-testid="privacy-contact-email">
              support@coparentconsole.com
            </a>.
          </p>
          <p>
            This Privacy Policy explains what personal data we collect, why we
            collect it, how we use, share and protect it, and the rights you
            have under the EU General Data Protection Regulation (GDPR) and the
            Irish Data Protection Acts 2018 (together, &quot;Data Protection
            Law&quot;).
          </p>
        </Section>

        <Section title="2. Sensitive nature of the data" id="sensitive-data">
          <p>
            We recognise that the data stored within CoParent Console may be
            sensitive in nature, including information relating to children,
            family arrangements, and financial matters. We take appropriate
            technical and organisational measures to protect this data.
          </p>
        </Section>

        <Section title="3. Personal data we collect" id="data-collected">
          <Sub title="Account data">
            <p>
              Your name, email address, securely hashed password, optional
              two-factor authentication secret, household identifier, role,
              and account creation date.
            </p>
          </Sub>
          <Sub title="Content you create">
            <p>
              Messages, calendar events, expense records and attached receipts,
              journal entries (including those you mark as private), uploaded
              documents, child profile details (name, date of birth, school,
              medical notes, routines, emergency contacts, general notes), and
              the metadata needed to organise them.
            </p>
          </Sub>
          <Sub title="Audit and security data">
            <p>
              An immutable audit log of actions taken within your household, so
              the record stays reliable and tamper-evident.
            </p>
          </Sub>
          <Sub title="Technical data">
            <p>
              IP address, user-agent, login timestamps, and similar metadata
              strictly necessary to operate and secure the Service.
            </p>
          </Sub>
          <Sub title="Payment data">
            <p>
              When you subscribe, your card details are handled directly by
              our regulated payment provider (Stripe). We never see or store
              your card number. We retain only billing metadata such as
              subscription status, last four digits of the card, billing
              country, and invoice history.
            </p>
          </Sub>
        </Section>

        <Section title="4. Lawful basis for processing (GDPR Article 6)" id="lawful-basis">
          <p>We process your personal data on the following lawful bases:</p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li>
              <strong>Contract</strong> — to provide the Service you sign up
              for (authentication, messaging, scheduling, expenses, records,
              exports, mediator and solicitor invites, and billing).
            </li>
            <li>
              <strong>Legitimate interests</strong> — to keep the Service
              secure and reliable (fraud prevention, abuse detection, audit
              logging, backups) and to operate and improve the product in ways
              you would reasonably expect. We balance these interests against
              your rights and freedoms.
            </li>
            <li>
              <strong>Consent</strong> — where you opt in to a specific feature
              (for example, enabling two-factor authentication or granting
              view-only access to a mediator or solicitor). You can withdraw
              consent at any time without affecting the lawfulness of earlier
              processing.
            </li>
            <li>
              <strong>Legal obligation</strong> — where we must respond to
              lawful requests from regulators, courts or law-enforcement.
            </li>
          </ul>
          <p>We do not sell your personal data and we do not use your content to train third-party AI models.</p>
        </Section>

        <Section title="5. Sharing and disclosure" id="sharing">
          <p>We share personal data only with:</p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li>
              <strong>Your co-parent</strong>, within the same household, in
              line with the permissions of the Service (for example, shared
              journal entries are visible to them, while private ones are not).
            </li>
            <li>
              <strong>Mediators or solicitors</strong> you explicitly invite
              using a time-limited view-only code.
            </li>
            <li>
              <strong>Processors</strong> acting on our instructions under
              written data-processing terms — cloud-hosting and database
              providers, transactional email delivery, object-storage
              providers, payment processing (Stripe) and error-monitoring
              providers. A current list is available on request.
            </li>
            <li>
              <strong>Authorities</strong> where required by law, court order
              or to protect the vital interests of a person.
            </li>
          </ul>
        </Section>

        <Section title="6. Third-party access" id="third-party-access">
          <p>
            Access granted to third parties is controlled by the user and can
            be revoked at any time. Third parties (such as solicitors and
            mediators) are provided with view-only access unless explicitly
            stated otherwise.
          </p>
          <p>
            You can revoke a third-party invitation from your Settings at any
            time. Once revoked, the third party loses access immediately.
          </p>
        </Section>

        <Section title="7. International transfers" id="transfers">
          <p>
            Some of our processors may store or process personal data outside
            the European Economic Area (EEA). Where this happens, we rely on
            safeguards approved under Data Protection Law — such as the EU
            Standard Contractual Clauses or an applicable adequacy decision —
            so your data remains protected to an EU standard.
          </p>
        </Section>

        <Section title="8. Data retention" id="retention">
          <p>
            We retain personal data only as long as necessary to provide the
            Service to you, comply with our legal obligations, resolve
            disputes and enforce our agreements.
          </p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li>
              Messages, calendar events, expenses, journal entries, documents
              and audit entries are retained while your account is active and
              for a reasonable wind-down period after account closure.
            </li>
            <li>
              Password-reset tokens are stored hashed and expire 30 minutes
              after issue; they are deleted from our database after expiry.
            </li>
            <li>
              Backups are retained for up to 30 days for disaster recovery;
              deleted data falls out of backups within that window.
            </li>
            <li>
              Billing records may be retained for up to 7 years to meet Irish
              tax and accounting obligations.
            </li>
          </ul>
          <p>
            You can request export or deletion of your account data at any
            time (see &quot;Your rights&quot; below).
          </p>
        </Section>

        <Section title="9. Security" id="security">
          <p>
            We apply administrative, technical and organisational measures
            appropriate to the risks, including:
          </p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li>Encryption in transit using HTTPS/TLS for all traffic.</li>
            <li>Secure storage with passwords stored as bcrypt hashes; we never write plaintext passwords to disk.</li>
            <li>Access controls scoped per household and per child, with least-privilege staff and processor access.</li>
            <li>Optional TOTP-based two-factor authentication.</li>
            <li>Object-storage files retrieved only through authenticated endpoints.</li>
            <li>An immutable audit log across household actions.</li>
          </ul>
          <p>
            No service is entirely secure. If we become aware of a personal
            data breach that is likely to result in a risk to your rights and
            freedoms, we will notify you and the Data Protection Commission
            (DPC) without undue delay, in line with Data Protection Law.
          </p>
        </Section>

        <Section title="10. Your rights" id="your-rights">
          <p>Subject to Data Protection Law, you have the right to:</p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li><strong>Access</strong> the personal data we hold about you.</li>
            <li><strong>Rectification</strong> — correct inaccurate or incomplete data (note: messages are intentionally immutable; this right applies to profile data and similar non-record content).</li>
            <li><strong>Erasure</strong> — request deletion of your personal data, subject to our retention obligations.</li>
            <li><strong>Restrict processing</strong> in certain circumstances.</li>
            <li><strong>Data portability</strong> — receive your personal data in a portable format. We provide PDF exports and records bundles in-app.</li>
            <li><strong>Object</strong> to processing based on legitimate interests.</li>
            <li><strong>Withdraw consent</strong> where processing is based on it.</li>
            <li>
              <strong>Lodge a complaint</strong> with the Irish Data Protection
              Commission (DPC) at{" "}
              <a href="https://www.dataprotection.ie" className="text-[#1F9FA4] hover:text-[#178085]" target="_blank" rel="noreferrer">
                dataprotection.ie
              </a>.
            </li>
          </ul>
        </Section>

        <Section title="11. Data requests contact" id="dsr-contact">
          <p>
            If you have questions or wish to exercise your data rights,
            contact us at{" "}
            <a href="mailto:support@coparentconsole.com" className="text-[#1F9FA4] hover:text-[#178085]">
              support@coparentconsole.com
            </a>. We will respond within one month.
          </p>
        </Section>

        <Section title="12. Cookies and similar technologies" id="cookies">
          <p>
            We use a small number of strictly necessary cookies and
            local-storage items to keep you signed in and to remember your
            preferences. We do not use advertising cookies or cross-site
            tracking.
          </p>
        </Section>

        <Section title="13. Children's data" id="childrens-data">
          <p>
            CoParent Console is intended for adults. It is not designed for
            use by children, and we do not allow children to create accounts.
          </p>
          <p>
            CoParent Console does, however, hold information about children
            (for example in child profiles and calendar events). That
            information is provided and controlled by the adult account
            holders. You are responsible for the information you input about
            children, and for ensuring you have the legal authority to do so.
          </p>
        </Section>

        <Section title="14. Mediator and solicitor access" id="mediator-access">
          <p>
            If you invite a mediator or solicitor using a view-only invite
            code, you are the controller of the data they see, and they
            access it on your instructions. Their access expires automatically
            at the end of the period you choose and can be revoked at any time
            from Settings.
          </p>
        </Section>

        <Section title="15. Changes to this Policy" id="changes">
          <p>
            We may update this Policy from time to time. Material changes
            will be notified in-app or by email. The &quot;Last updated&quot;
            date at the top reflects the most recent revision.
          </p>
        </Section>

        <Section title="16. Contact" id="contact">
          <p>
            Questions, requests or complaints:{" "}
            <a href="mailto:support@coparentconsole.com" className="text-[#1F9FA4] hover:text-[#178085]">
              support@coparentconsole.com
            </a>.
          </p>
        </Section>
      </main>
    </div>
  );
}
